<template>
  <div v-if="isVisible" 
    style="box-shadow: 0 -4px 4px 0 rgba(0, 0, 0, 0.25)"
    class="bg-zt-normal h-[228px] absolute text-sm text-white left-0 z-modal w-[calc(100%+30px)] -ml-4 top-[50px] xl:mx-0 xl:w-[244px]">
    <div class="suggestions-container" ref="suggestionsContainer" @scroll="handleScroll">
      <div v-if="loading && users.length === 0" class="p-4 grid place-items-center h-full">
        <span class="animate-bounce">
          Ładowanie...
        </span>
      </div>
      <div v-else-if="users.length === 0 && searchQuery.length >= 3" class="p-4 grid place-items-center h-full">
        Nie znaleziono użytkowników
      </div>
      <div v-else>
        <div v-for="(user, index) in discussionParticipants" 
             :key="`participant-${user.ID}`"
             class="flex gap-x-[11px] cursor-pointer zt-hover p-[6px] items-center border-b border-zt-light last:border-0"
             :class="{ 'border-b-0' :  otherUsers.length > 0 && index === discussionParticipants.length -1 }"
             :ref="`item-${index}`"
             @click="selectUser(user)"
             @mouseenter="selectedIndex = index">
          <img :src="user.avatar" class="w-[30px] h-[30px] rounded-full" alt="Avatar" />
          <span class="user-name">{{ user.display_name }}</span>
        </div>
        
        <div v-if="discussionParticipants.length > 0 && otherUsers.length > 0" class="border-b border-px border-zt-light border-dashed" :ref="`item-${discussionParticipants.length}`"></div>
        
        <div v-for="(user, index) in otherUsers" 
             :key="`other-${user.ID}`"
             class="flex gap-x-[11px] p-[6px] cursor-pointer zt-hover items-center border-b border-zt-light last:border-0"
             :ref="`item-${discussionParticipants.length + (hasSeparator ? 1 : 0) + index}`"
             @click="selectUser(user)"
             @mouseenter="selectedIndex = discussionParticipants.length + (hasSeparator ? 1 : 0) + index">
          <img :src="user.avatar" class="w-[30px] h-[30px] rounded-full" alt="Avatar" />
          <span class="user-name">{{ user.display_name }}</span>
        </div>
        <div v-if="loading && otherUsers.length > 0" class="p-4">
          Ładowanie więcej...
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'UserMentionSuggestions',
  inject: ['WP'],
  props: {
    betId: {
      type: Number,
      required: true
    },
    searchQuery: {
      type: String,
      default: ''
    },
    isVisible: {
      type: Boolean,
      default: false
    },
  },
  emits: ['select-user', 'close'],
  data() {
    return {
      users: [],
      loading: false,
      selectedIndex: 0,
      hasSeparator: false,
      offset: 0,
      hasMore: true,
      loadingMore: false,
      scrollThrottleTimeout: null
    };
  },
  computed: {
    discussionParticipants() {
      return this.users.filter(user => user.is_discussion_participant);
    },
    otherUsers() {
      return this.users.filter(user => !user.is_discussion_participant);
    },
    totalItems() {
      return this.discussionParticipants.length + this.otherUsers.length + (this.hasSeparator ? 1 : 0);
    }
  },
  watch: {
    searchQuery: {
      handler(newQuery, oldQuery) {
        if (newQuery !== oldQuery) {
          this.resetAndFetch();
        }
      },
      immediate: true
    },
    isVisible(newValue) {
      if (newValue) {
        this.selectedIndex = 0;
        this.resetAndFetch();
      }
    },
  },
  methods: {
    resetAndFetch() {
      this.offset = 0;
      this.users = [];
      this.hasMore = true;
      this.hasSeparator = false;
      this.fetchSuggestions();
    },
    async fetchSuggestions() {
      if (!this.isVisible || this.loading || !this.hasMore) return;
      
      this.loading = true;
      
      try {
        const response = await axios.post(this.WP.ajaxUrl, {
          endpoint: 'api_get_user_suggestions_for_mention',
          bet_id: this.betId,
          search: this.searchQuery,
          offset: this.offset,
          limit: 5
        });
        
        if (response.data.success) {
          const newUsers = response.data.data.users || [];
          
          if (this.offset === 0) {
            this.users = newUsers;
            this.hasSeparator = response.data.data.has_separator || false;
          } else {
            this.users = [...this.users, ...newUsers];
            if (!this.hasSeparator && response.data.data.has_separator) {
              this.hasSeparator = true;
            }
          }
          
          this.hasMore = response.data.data.has_more || false;
          this.offset += newUsers.length;
          
          if (this.offset === newUsers.length) {
            this.selectedIndex = 0;
          }
        } else {
          if (this.offset === 0) {
            this.users = [];
            this.hasSeparator = false;
          }
          this.hasMore = false;
        }
      } catch (error) {
        console.error('Error fetching user suggestions:', error);
        if (this.offset === 0) {
          this.users = [];
          this.hasSeparator = false;
        }
        this.hasMore = false;
      } finally {
        this.loading = false;
      }
    },
    handleScroll(event) {
      if (this.scrollThrottleTimeout) {
        clearTimeout(this.scrollThrottleTimeout);
      }
      
      this.scrollThrottleTimeout = setTimeout(() => {
        const container = event.target;
        const scrollTop = container.scrollTop;
        const scrollHeight = container.scrollHeight;
        const clientHeight = container.clientHeight;
        
        if (scrollHeight - scrollTop - clientHeight < 20 && this.hasMore && !this.loading) {
          this.fetchSuggestions();
        }
      }, 100);
    },
    selectUser(user) {
      this.$emit('select-user', user);
    },
    navigateUp() {
      if (this.selectedIndex > 0) {
        if (this.hasSeparator && this.selectedIndex === this.discussionParticipants.length + 1) {
          this.selectedIndex = this.discussionParticipants.length - 1;
        } else {
          this.selectedIndex--;
        }
        this.scrollToSelected();
      }
    },
    navigateDown() {
      if (this.selectedIndex < this.totalItems - 1) {
        if (this.hasSeparator && this.selectedIndex === this.discussionParticipants.length - 1) {
          this.selectedIndex = this.discussionParticipants.length + 1;
        } else {
          this.selectedIndex++;
        }
        this.scrollToSelected();
      }
    },
    scrollToSelected() {
      this.$nextTick(() => {
        const selectedElement = this.$refs[`item-${this.selectedIndex}`];
        if (selectedElement && selectedElement[0]) {
          selectedElement[0].scrollIntoView({
            behavior: 'smooth',
            block: 'nearest'
          });
        }
      });
    },
    selectCurrentUser() {
      const allItems = [...this.discussionParticipants];
      if (this.hasSeparator) {
        allItems.push(null);
      }
      allItems.push(...this.otherUsers);
      
      const selectedUser = allItems[this.selectedIndex];
      if (selectedUser && selectedUser.ID) {
        this.selectUser(selectedUser);
      }
    },
    close() {
      this.$emit('close');
    },
    cleanup() {
      if (this.scrollThrottleTimeout) {
        clearTimeout(this.scrollThrottleTimeout);
        this.scrollThrottleTimeout = null;
      }
    }
  },
  beforeUnmount() {
    this.cleanup();
  }
};
</script>

<style scoped>
.suggestions-container {
  padding: 8px 0;
  height: 100%;
  max-height: 228px;
  overflow-y: auto;
}

::-webkit-scrollbar {
  width: 6px;
}

::-webkit-scrollbar-track {
  background: #263042;
  border-radius: 3px;
}

::-webkit-scrollbar-thumb {
  background: #93A3BA;
  border-radius: 3px;
}
</style>
